/**
 * Utility functions for handling image URLs and optimization
 */

/**
 * Converts a relative image URL to an absolute URL
 * If the URL is already absolute, returns it as-is
 * @param {string} imageUrl - The image URL (can be relative or absolute)
 * @returns {string} - The absolute image URL
 */
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) {
    return "/placeholder.jpg"; // Default placeholder if no image
  }

  // If already an absolute URL (starts with http:// or https://), return as-is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // If it's a data URL (base64), return as-is
  if (imageUrl.startsWith("data:")) {
    return imageUrl;
  }

  // For relative URLs, prepend the backend server URL
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  // Remove leading slash if present to avoid double slashes
  const cleanUrl = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;

  return `${backendUrl}${cleanUrl}`;
};

/**
 * Get the compressed WebP image URL for a specific size
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Image size: 'thumbnail', 'medium', 'large', or 'original'
 * @returns {string} URL to the compressed image
 */
export const getCompressedImageUrl = (imageUrl, size = "medium") => {
  if (!imageUrl) {
    return getImageUrl("/placeholder.jpg");
  }

  // External URLs or data URLs: return as-is (no compressed variant)
  if (
    imageUrl.startsWith("http://") ||
    imageUrl.startsWith("https://") ||
    imageUrl.startsWith("data:")
  ) {
    return getImageUrl(imageUrl);
  }

  // Extract path without extension and strip any existing size suffix
  let base = imageUrl.replace(/\.[^.]+$/, "");
  const suffixMatch = base.match(/-(original|thumbnail|medium|large)$/);
  if (suffixMatch) base = base.slice(0, -suffixMatch[0].length);

  const sizedUrl = `${base}-${size === "original" ? "original" : size}.webp`;
  return getImageUrl(sizedUrl);
};

/**
 * Get responsive image URLs for different devices
 * @param {string} imageUrl - Original image URL
 * @returns {object} Object with sizes for different breakpoints
 */
export const getResponsiveImages = (imageUrl) => {
  return {
    thumbnail: getCompressedImageUrl(imageUrl, "thumbnail"),
    mobile: getCompressedImageUrl(imageUrl, "medium"),
    tablet: getCompressedImageUrl(imageUrl, "large"),
    desktop: getCompressedImageUrl(imageUrl, "original"),
  };
};

/**
 * Create a responsive image srcSet string
 * @param {string} imageUrl - Original image URL
 * @returns {string} srcSet string for img element
 */
export const getImageSrcSet = (imageUrl) => {
  if (!imageUrl) return "";

  const thumbnail = getCompressedImageUrl(imageUrl, "thumbnail");
  const medium = getCompressedImageUrl(imageUrl, "medium");
  const large = getCompressedImageUrl(imageUrl, "large");
  const original = getCompressedImageUrl(imageUrl, "original");

  return `${thumbnail} 200w, ${medium} 500w, ${large} 1000w, ${original} 2000w`;
};

/**
 * Get optimized image sizes attribute
 * @returns {string} sizes attribute for img element
 */
export const getImageSizes = () => {
  return "(max-width: 480px) 100vw, (max-width: 768px) 50vw, 33vw";
};

/**
 * Preload multiple images for performance
 * @param {array} imageUrls - Array of image URLs to preload
 */
export const preloadImages = (imageUrls) => {
  imageUrls.forEach((url) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = getImageUrl(url);
    document.head.appendChild(link);
  });
};
